import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  X, Clock, Hash, Mail, FileText, Play, Trash2, Pencil,
  CheckCircle2, XCircle, Loader2, Save, Calendar,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { agentTasksApi, type AgentTask, type AgentTaskRun } from "@/api/agent-tasks";
import { agentChannelsApi } from "@/api/agent-channels";
import { queryKeys } from "@/lib/query-keys";

interface TaskDetailDrawerProps {
  task: AgentTask | null;
  tenantId: string;
  onClose: () => void;
}

/** Preset cron patterns for quick selection. */
const CRON_PRESETS: { value: string; label: string }[] = [
  { value: "0 9 * * 1-5", label: "Weekdays at 09:00" },
  { value: "0 17 * * 1-5", label: "Weekdays at 17:00" },
  { value: "0 9 * * *", label: "Every day at 09:00" },
  { value: "0 */4 * * *", label: "Every 4 hours" },
  { value: "0 * * * *", label: "Every hour" },
  { value: "*/15 * * * *", label: "Every 15 minutes" },
  { value: "0 0 * * 1", label: "Every Monday at midnight" },
  { value: "0 0 1 * *", label: "First of the month" },
];

/** Render a cron expression as English. Falls back to the raw expression. */
function cronToText(expr: string | null): string {
  if (!expr) return "—";
  const preset = CRON_PRESETS.find((p) => p.value === expr);
  if (preset) return preset.label;

  const parts = expr.split(" ");
  if (parts.length === 5) {
    const [min, hr, dom, mon, dow] = parts;
    if (dom === "*" && mon === "*" && /^\d+$/.test(min) && /^\d+$/.test(hr)) {
      const time = `${hr.padStart(2, "0")}:${min.padStart(2, "0")}`;
      if (dow === "*") return `Daily at ${time}`;
      if (dow === "1-5") return `Weekdays at ${time}`;
      if (/^\d+$/.test(dow)) {
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        return `${days[parseInt(dow, 10)]} at ${time}`;
      }
    }
  }
  return expr;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Guess the destination from task config and prompt keywords. */
function describeDestination(task: AgentTask): { icon: typeof Hash; label: string; hint?: string } {
  if (task.announceChannelName) {
    return { icon: Hash, label: `#${task.announceChannelSlug || task.announceChannelName}`, hint: "channel" };
  }
  const prompt = (task.taskPrompt || "").toLowerCase();
  if (prompt.includes("email") || prompt.includes("mail ") || prompt.includes("send to")) {
    return { icon: Mail, label: "via agent (email)", hint: "in the task prompt" };
  }
  return { icon: FileText, label: "Task Reports (default)", hint: "no channel set" };
}

export function TaskDetailDrawer({ task, tenantId, onClose }: TaskDetailDrawerProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<{
    title: string;
    description: string;
    cronExpression: string;
    timezone: string;
    taskPrompt: string;
    enabled: boolean;
    announceChannelId: string;
  } | null>(null);

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title,
        description: task.description || "",
        cronExpression: task.cronExpression || "",
        timezone: task.timezone || "UTC",
        taskPrompt: task.taskPrompt || "",
        enabled: task.enabled,
        announceChannelId: task.announceChannelId || "",
      });
      setEditing(false);
    }
  }, [task]);

  // Fetch task details with runs
  const { data: taskDetail } = useQuery({
    queryKey: task ? queryKeys.agentTasks.detail(task.id) : ["agent-tasks", "detail", "none"],
    queryFn: () => agentTasksApi.get(task!.id),
    enabled: !!task,
    refetchInterval: task ? 5000 : false,
  });

  // Fetch channels for this agent (for the destination dropdown)
  const { data: channels = [] } = useQuery({
    queryKey: ["agent-channels", task?.agentId],
    queryFn: () => agentChannelsApi.list(task!.agentId),
    enabled: !!task && editing,
  });

  const updateMutation = useMutation({
    mutationFn: (patch: Record<string, unknown>) => agentTasksApi.update(task!.id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agentTasks.byCompany(tenantId) });
      setEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => agentTasksApi.delete(task!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agentTasks.byCompany(tenantId) });
      onClose();
    },
  });

  const triggerMutation = useMutation({
    mutationFn: () => agentTasksApi.trigger(task!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agentTasks.byCompany(tenantId) });
    },
  });

  if (!task || !form) return null;

  const runs = taskDetail?.runs || [];
  const destination = describeDestination(task);
  const DestIcon = destination.icon;

  const save = () => {
    updateMutation.mutate({
      title: form.title,
      description: form.description || undefined,
      cronExpression: form.cronExpression,
      timezone: form.timezone,
      taskPrompt: form.taskPrompt || undefined,
      enabled: form.enabled,
      announceChannelId: form.announceChannelId || null,
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/40 animate-fade-in"
      />
      {/* Right-side drawer */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-[560px] flex flex-col bg-background border-l border-border shadow-2xl animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Clock size={18} className="text-accent shrink-0" />
            <h2 className="text-base font-semibold text-text-primary truncate">
              {task.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-surface-hover text-text-muted"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {!editing ? (
            <div className="p-5 space-y-5">
              {/* Summary cards */}
              <div className="grid grid-cols-2 gap-3">
                <InfoCard
                  icon={Calendar}
                  label="Schedule"
                  value={cronToText(task.cronExpression)}
                  hint={task.cronExpression || undefined}
                />
                <InfoCard
                  icon={DestIcon}
                  label="Destination"
                  value={destination.label}
                  hint={destination.hint}
                />
                <InfoCard
                  icon={Clock}
                  label="Next run"
                  value={task.enabled ? formatDateTime(task.nextRun) : "Paused"}
                />
                <InfoCard
                  icon={task.lastResult?.success ? CheckCircle2 : XCircle}
                  label="Last run"
                  value={formatDateTime(task.lastRunAt)}
                  hint={task.lastResult?.success === false ? task.lastResult.error : undefined}
                />
              </div>

              {/* Status */}
              <div className="flex items-center gap-2">
                <Badge variant={task.enabled ? "success" : "secondary"}>
                  {task.enabled ? "Active" : "Paused"}
                </Badge>
                <Badge variant="default">{task.type}</Badge>
                <span className="text-[11px] text-text-muted">tz: {task.timezone}</span>
              </div>

              {/* Prompt — always shown. Explains the fallback when empty so
                   users know agent-registered tasks rely on the agent's own
                   workspace context unless a custom prompt is set. */}
              <div>
                <h3 className="text-[11px] font-medium uppercase tracking-wide text-text-muted mb-1.5">
                  Task Prompt
                  {task.createdBy === "agent" && !task.taskPrompt && (
                    <span className="ml-2 text-text-muted normal-case tracking-normal">
                      (agent-registered · uses fallback)
                    </span>
                  )}
                </h3>
                {task.taskPrompt ? (
                  <div className="rounded-lg border border-border bg-surface/40 p-3 text-sm text-text-primary whitespace-pre-wrap">
                    {task.taskPrompt}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-border bg-surface/20 p-3 text-xs text-text-muted">
                    <p className="mb-1.5">
                      No custom prompt saved. When this task fires, the platform
                      sends the agent:
                    </p>
                    <pre className="text-[11px] text-text-secondary font-mono bg-background/60 rounded px-2 py-1.5 whitespace-pre-wrap">
Execute scheduled task: "{task.title}"
{task.description ? `\n${task.description}` : ""}
                    </pre>
                    <p className="mt-1.5">
                      The agent fills in the details from its own workspace
                      (SOUL.md, AGENTS.md, skills). Click <b>Edit</b> to write a
                      custom prompt that overrides this fallback.
                    </p>
                  </div>
                )}
              </div>

              {/* Description */}
              {task.description && (
                <div>
                  <h3 className="text-[11px] font-medium uppercase tracking-wide text-text-muted mb-1.5">
                    Description
                  </h3>
                  <p className="text-sm text-text-secondary">{task.description}</p>
                </div>
              )}

              {/* Recent runs */}
              <div>
                <h3 className="text-[11px] font-medium uppercase tracking-wide text-text-muted mb-2">
                  Recent runs ({runs.length})
                </h3>
                {runs.length === 0 ? (
                  <p className="text-sm text-text-muted">No runs yet.</p>
                ) : (
                  <div className="flex flex-col gap-1">
                    {runs.slice(0, 15).map((run) => (
                      <RunRow key={run.id} run={run} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Edit form */
            <div className="p-5 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">Title</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                  Schedule
                </label>
                <select
                  value={CRON_PRESETS.find((p) => p.value === form.cronExpression) ? form.cronExpression : ""}
                  onChange={(e) => e.target.value && setForm({ ...form, cronExpression: e.target.value })}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm mb-1.5"
                >
                  <option value="">— Choose a preset —</option>
                  {CRON_PRESETS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <Input
                  value={form.cronExpression}
                  onChange={(e) => setForm({ ...form, cronExpression: e.target.value })}
                  placeholder="Or type a custom cron (5 fields)"
                  className="font-mono text-xs"
                />
                <p className="mt-1 text-[10px] text-text-muted">
                  Parses as: <span className="text-text-secondary">{cronToText(form.cronExpression)}</span>
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">Timezone</label>
                <select
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                >
                  <option value="UTC">UTC</option>
                  <option value="Europe/London">Europe/London (UK)</option>
                  <option value="America/New_York">America/New_York (ET)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (PT)</option>
                  <option value="Europe/Paris">Europe/Paris (CET)</option>
                  <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                  Output channel
                </label>
                <select
                  value={form.announceChannelId}
                  onChange={(e) => setForm({ ...form, announceChannelId: e.target.value })}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                >
                  <option value="">— Task Reports (default) —</option>
                  {channels.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.iconEmoji ? `${c.iconEmoji} ` : ""}
                      {c.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[10px] text-text-muted">
                  Where the autonomous result will be posted. If your task prompt
                  asks the agent to email / message / notify, it will do so in
                  addition to posting here.
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                  Task prompt
                </label>
                <textarea
                  value={form.taskPrompt}
                  onChange={(e) => setForm({ ...form, taskPrompt: e.target.value })}
                  rows={5}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">Description</label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="h-4 w-4"
                />
                <span className="text-sm text-text-primary">Enabled (runs on schedule)</span>
              </label>

              {updateMutation.isError && (
                <p className="text-xs text-red-500">
                  {(updateMutation.error as Error).message || "Failed to save"}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center gap-2 px-5 py-3 border-t border-border shrink-0 bg-surface/30">
          {editing ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={save}
                disabled={updateMutation.isPending || !form.cronExpression}
                className="ml-auto"
              >
                {updateMutation.isPending ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Save size={14} className="mr-1.5" />
                )}
                Save changes
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => triggerMutation.mutate()}
                disabled={triggerMutation.isPending}
              >
                {triggerMutation.isPending ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Play size={14} className="mr-1.5" />
                )}
                Run now
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
              >
                <Pencil size={14} className="mr-1.5" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (confirm(`Delete task "${task.title}"? This cannot be undone.`)) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
                className="ml-auto text-red-500 hover:bg-red-500/10"
              >
                <Trash2 size={14} />
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function InfoCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Clock;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface/40 p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={11} className="text-text-muted" />
        <span className="text-[10px] uppercase tracking-wide font-medium text-text-muted">
          {label}
        </span>
      </div>
      <p className="text-sm font-medium text-text-primary truncate">{value}</p>
      {hint && <p className="text-[10px] text-text-muted truncate mt-0.5">{hint}</p>}
    </div>
  );
}

function RunRow({ run }: { run: AgentTaskRun }) {
  const success = run.status === "success";
  const summary = run.result?.summary || run.result?.error || "";
  return (
    <div className={cn(
      "flex items-start gap-2 rounded-md px-2.5 py-1.5 text-xs",
      success ? "hover:bg-surface-hover" : "bg-red-500/5",
    )}>
      {success ? (
        <CheckCircle2 size={12} className="text-emerald-400 shrink-0 mt-0.5" />
      ) : (
        <XCircle size={12} className="text-red-400 shrink-0 mt-0.5" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-text-primary">
          {new Date(run.startedAt).toLocaleString(undefined, {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
          <span className="ml-2 text-text-muted">
            {run.durationMs != null && `${Math.round(run.durationMs / 1000)}s`}
          </span>
        </p>
        {summary && (
          <p className="text-text-muted truncate">{summary}</p>
        )}
      </div>
    </div>
  );
}
